// THE CALENDAR IN AN AI ANSWER — the agenda view the assistant mounts in its
// thread (workspace functions/assistantAppViews.js, the `views[]` contract).
//
// calendarAiViews() is the calendar's view PROVIDER: the shell reads it from
// the EAGER slice (the calendar package is lazy — the shell folds its context
// before the view mounts, `fold: 'calendar'`). One view:
//
//   calendar-agenda   the events of a date range, day by day — the member's
//                     own, a teammate's, or the whole team's; a row opens the
//                     event's own detail sheet (openEventDetailModal).
//
// The rows come from the calendar's own data: `root.events` (the shell's
// calendar window) widened to the range's months by ensureCalendarWindow, the
// day match by getEventsForDay (recurrences included). Nothing here writes.
//
// Frank note: el.call serializes each export on its own (FA204) — every export
// is self-contained.

export function calendarAiViews() {
  return {
    app: {
      id: 'system/calendar',
      navbar: [{ label: 'shell.app.calendar', route: '/calendar', icon: 'date outline' }],
      apps: [{ icon: 'date outline' }]
    },
    fold: 'calendar',
    views: [
      {
        id: 'calendar-agenda',
        title: 'calendar.agenda.title',
        describe:
          'Calendar events of a date range, day by day; range = the days, who = my events or the team, person = one teammate.',
        component: 'CalAgendaEmbed',
        icon: 'date outline',
        hue: 'calendar',
        size: 'l',
        params: {
          range: {
            type: 'range',
            // Relative presets the server turns into days in the member's
            // clock (server ai-boards/rangePresets.js); the default is what the
            // card draws when the ask names no days, so the answer says them.
            values: ['today', 'next7days', 'thisWeek', 'nextWeek'],
            default: 'next7days',
            describe:
              'YYYY-MM-DD..YYYY-MM-DD, or today | next7days | thisWeek | nextWeek; a week is Sunday..Saturday; default the next 7 days'
          },
          who: {
            type: 'enum',
            values: ['me', 'team'],
            // Bundle keys: the catalog sends them as { en, ka } words.
            labels: { me: 'calendar.agenda.who.me', team: 'calendar.agenda.who.team' },
            default: 'me'
          },
          person: { type: 'string', describe: 'a teammate name or email; overrides who' }
        },
        link: { route: '/calendar', query: { date: 'range' } }
      }
    ]
  }
}

// The days of the range: { from, to } as YYYY-MM-DD. A missing or malformed
// range is the next 7 days from `now`; a range over 31 days is cut to 31.
export function calendarAgendaRange(params, now) {
  const p = params && typeof params === 'object' ? params : {}
  const pad = (n) => String(n).padStart(2, '0')
  const iso = (d) => d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
  const parse = (s) => {
    if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null
    const [y, m, d] = s.split('-').map(Number)
    const out = new Date(y, m - 1, d)
    return out.getMonth() === m - 1 ? out : null
  }
  const base = now instanceof Date ? now : new Date(Number(now) || Date.now())
  let from = null
  let to = null
  if (typeof p.range === 'string' && p.range.includes('..')) {
    const [a, b] = p.range.split('..')
    from = parse(a)
    to = parse(b)
  } else if (typeof p.range === 'string') {
    // A preset that reaches the view as itself — a Fly page's section (the
    // board path embeds the model's value as it is), an older server — in
    // this device's clock. A WEEK is the app's own week, Sunday..Saturday:
    // the rule of the week grid (calendarUtils.js getWeekDays — the
    // calendarAgendaEmbed test holds the two to the same days). It was
    // Monday-based here: on Sunday 18 Oct Fly's "this week" read 12–18 Oct
    // while the calendar's week was 18–24 (Fly QA round 2, R5).
    const day = new Date(base.getFullYear(), base.getMonth(), base.getDate())
    const sinceStart = day.getDay()
    const at = (n) => new Date(day.getFullYear(), day.getMonth(), day.getDate() + n)
    const span = {
      today: [0, 0],
      next7days: [0, 6],
      thisWeek: [-sinceStart, 6 - sinceStart],
      nextWeek: [7 - sinceStart, 13 - sinceStart]
    }[p.range]
    if (span) {
      from = at(span[0])
      to = at(span[1])
    }
  }
  if (!from || !to || to < from) {
    from = new Date(base.getFullYear(), base.getMonth(), base.getDate())
    to = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6)
  }
  const max = new Date(from.getFullYear(), from.getMonth(), from.getDate() + 30)
  if (to > max) to = max
  return { from: iso(from), to: iso(to) }
}

// Widen the calendar window to every month the range touches (at most two
// reads — a 31-day range spans two months). Returns the ensure promises.
export function calendarAgendaEnsure(params) {
  const el = this
  const root = el && el.state && el.state.root
  if (!root) return []
  const { from, to } = el.call('calendarAgendaRange', params)
  const months = []
  const [fy, fm] = from.split('-').map(Number)
  const [ty, tm] = to.split('-').map(Number)
  months.push([fy, fm - 1])
  if (ty !== fy || tm !== fm) months.push([ty, tm - 1])
  return months.map(([y, m]) => el.call('ensureCalendarWindow', root, y, m))
}

// The agenda: [{ key, label, rows: [{ key, title, meta, open }] }] — only the
// days with events, in order. null while the calendar window is unread.
export function calendarAgendaDays(root, params) {
  const el = this
  const r = root || (el && el.state && el.state.root)
  if (!r || !Array.isArray(r.events)) return null
  const p = params && typeof params === 'object' ? params : {}
  const { from, to } = el.call('calendarAgendaRange', p)
  const me = String(r.currentUserEmail || '').toLowerCase()
  // `person` names one teammate: an email, or a name the roster knows.
  let who = null
  const person = typeof p.person === 'string' ? p.person.trim().toLowerCase() : ''
  if (person) {
    if (person.includes('@')) who = person
    else {
      const people = Array.isArray(r.people) ? r.people : []
      const hit = people.find((x) => {
        const name = String(
          (x && (x.name || [x.firstName, x.lastName].filter(Boolean).join(' '))) || ''
        ).toLowerCase()
        return name && (name === person || name.split(' ').includes(person))
      })
      who = hit && hit.email ? String(hit.email).toLowerCase() : '\u0000'
    }
  } else if (p.who !== 'team') who = me
  const emailsOf = (ev) => {
    const out = [ev.googleCalendarId, ev.creatorEmail]
    for (const i of Array.isArray(ev.invitees) ? ev.invitees : []) {
      out.push(typeof i === 'string' ? i : i && i.email)
    }
    return out.filter(Boolean).map((x) => String(x).toLowerCase())
  }
  const events = who ? r.events.filter((ev) => ev && emailsOf(ev).includes(who)) : r.events
  const lang = el.call('getActiveLang') || 'en'
  const time = (ev) => {
    if (!ev.startTime) return String(el.call('polyglot', 'calendar.agenda.allDay'))
    const clock = (hhmm) => {
      const [h, m] = String(hhmm).split(':').map(Number)
      if (!Number.isFinite(h)) return String(hhmm)
      return el.call('formatTzTime', new Date(2000, 0, 1, h, m || 0).getTime(), undefined, lang)
    }
    return clock(ev.startTime) + (ev.endTime ? ' – ' + clock(ev.endTime) : '')
  }
  const DAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
  const MONTHS = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december'
  ]
  const out = []
  const [fy, fm, fd] = from.split('-').map(Number)
  const [ty, tm, td] = to.split('-').map(Number)
  const end = new Date(ty, tm - 1, td)
  for (
    let d = new Date(fy, fm - 1, fd);
    d <= end;
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
  ) {
    // One meeting synced from several calendars (the organiser's and an
    // invitee's Google calendar) is ONE row: same title, time and place, as
    // the home "Next up" reads it (Fly QA on dev, 2026-10-18: "Team Leads"
    // twice on Mon 19 — records 85 and 330). An untitled event stays as it is.
    const seen = new Set()
    const list = el
      .call('getEventsForDay', events, d.getFullYear(), d.getMonth(), d.getDate())
      .filter((ev) => ev && !ev._isCrossTail)
      .filter((ev) => {
        if (!ev.title) return true
        const same = [ev.title, ev.startTime || '', ev.endTime || '', ev.location || ''].join('|')
        if (seen.has(same)) return false
        seen.add(same)
        return true
      })
      .sort((a, b) => String(a.startTime || '').localeCompare(String(b.startTime || '')))
    if (!list.length) continue
    const key = el.call('formatDate', d.getFullYear(), d.getMonth(), d.getDate())
    const label =
      el.call('polyglot', 'calendar.day.' + DAYS[d.getDay()]) +
      ' ' +
      d.getDate() +
      ' ' +
      el.call('polyglot', 'calendar.month.' + MONTHS[d.getMonth()])
    out.push({
      key,
      label,
      rows: list.map((ev, i) => ({
        key: String(ev.id || ev.googleEventId || key + '-' + i) + '@' + key,
        title: ev.title
          ? String(ev.title)
          : String(el.call('polyglot', 'calendar.agenda.untitled')),
        meta: time(ev),
        open: { call: 'openEventDetailModal', arg: ev }
      }))
    })
  }
  return out
}
